import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contractApi, estimateApi, projectApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  FileText, Search, Filter, Eye, Clock, CheckCircle, 
  AlertCircle, XCircle, FileCheck, MapPin, Euro, Calendar,
  Hash, User, ExternalLink, FolderOpen, Briefcase, MoreVertical, Trash2
} from 'lucide-react';

const STATUS_CONFIG = {
  bozza: { label: 'Bozza', color: 'bg-gray-100 text-gray-700', icon: FileText },
  in_attesa_firma: { label: 'In attesa firma', color: 'bg-blue-100 text-blue-700', icon: Clock },
  documenti_caricati: { label: 'Documenti caricati', color: 'bg-amber-100 text-amber-700', icon: FileCheck },
  in_validazione: { label: 'In validazione', color: 'bg-purple-100 text-purple-700', icon: Clock },
  approvato: { label: 'Approvato', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  documenti_mancanti: { label: 'Documenti mancanti', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  respinto: { label: 'Respinto', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const ContractsPage = () => {
  const { isAdmin, isProjectManager } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteDraft = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await contractApi.delete(deleteTarget.id);
      toast.success('Bozza eliminata');
      setContracts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Errore durante l\'eliminazione';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contractsRes, estimatesRes, projectsRes] = await Promise.all([
        contractApi.getAll(),
        estimateApi.getAll().catch(() => ({ data: [] })),
        projectApi.getAll().catch(() => ({ data: [] }))
      ]);
      setContracts(contractsRes.data);
      setEstimates(estimatesRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Errore nel caricamento dei contratti');
    } finally {
      setLoading(false);
    }
  };

  // Helper per ottenere numero preventivo
  const getEstimateNumber = (estimateId) => {
    if (!estimateId) return null;
    const estimate = estimates.find(e => e.id === estimateId);
    return estimate?.estimate_number || null;
  };

  // Helper per ottenere numero progetto
  const getProjectNumber = (projectId) => {
    if (!projectId) return null;
    const project = projects.find(p => p.id === projectId);
    return project?.project_number || null;
  };

  // Helper per ottenere indirizzo visualizzato
  const getDisplayAddress = (contract) => {
    if (contract.site_same_as_customer_address === false && contract.site_address) {
      return contract.site_address;
    }
    return contract.property_address || contract.customer_address || 'N/D';
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const displayAddress = getDisplayAddress(contract);
      const matchesSearch = !searchTerm || 
        contract.contract_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        displayAddress?.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesStatus = true;
      if (statusFilter === 'da_validare') {
        matchesStatus = ['documenti_caricati', 'in_validazione'].includes(contract.status);
      } else if (statusFilter !== 'all') {
        matchesStatus = contract.status === statusFilter;
      }
      
      return matchesSearch && matchesStatus;
    });
  }, [contracts, searchTerm, statusFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: contracts.length,
    pending: contracts.filter(c => ['documenti_caricati', 'in_validazione'].includes(c.status)).length,
    approved: contracts.filter(c => c.status === 'approvato').length,
    needsAction: contracts.filter(c => c.status === 'documenti_mancanti').length,
  }), [contracts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Contratti</h1>
          <p className="text-body-small mt-1">
            Gestisci i contratti e la validazione documenti
          </p>
        </div>
      </div>

      {/* Stats Cards - Clickable Filters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'all' ? 'ring-2 ring-gray-400 shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')}
          data-testid="filter-total"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{stats.total}</p>
                <p className="text-xs text-[#737373]">Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'da_validare' ? 'ring-2 ring-[#FF7819] shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'da_validare' ? 'all' : 'da_validare')}
          data-testid="filter-pending"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFF0E5] rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#FF7819]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{stats.pending}</p>
                <p className="text-xs text-[#737373]">Da validare</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'approvato' ? 'ring-2 ring-[#008A3D] shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'approvato' ? 'all' : 'approvato')}
          data-testid="filter-approved"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E5F7ED] rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#008A3D]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{stats.approved}</p>
                <p className="text-xs text-[#737373]">Approvati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'documenti_mancanti' ? 'ring-2 ring-[#B38C00] shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'documenti_mancanti' ? 'all' : 'documenti_mancanti')}
          data-testid="filter-needs-action"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFF9E5] rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-[#B38C00]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{stats.needsAction}</p>
                <p className="text-xs text-[#737373]">Doc. mancanti</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="card-abitalo">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
              <Input
                placeholder="Cerca per numero, cliente, indirizzo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-abitalo pl-10"
                data-testid="contracts-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="contracts-status-filter">
                <Filter className="w-4 h-4 mr-2 text-[#737373]" />
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="da_validare">Da validare</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="documenti_caricati">Documenti caricati</SelectItem>
                <SelectItem value="approvato">Approvato</SelectItem>
                <SelectItem value="documenti_mancanti">Documenti mancanti</SelectItem>
                <SelectItem value="respinto">Respinto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Cards — 2 per row desktop */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {filteredContracts.length === 0 ? (
          <Card className="card-abitalo xl:col-span-2">
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-[#DCDCDC]" />
              <p className="text-[#737373]">Nessun contratto trovato</p>
            </CardContent>
          </Card>
        ) : (
          filteredContracts.map((contract) => {
            const statusConfig = STATUS_CONFIG[contract.status] || STATUS_CONFIG.bozza;
            const displayAddress = getDisplayAddress(contract);
            const estimateNumber = getEstimateNumber(contract.estimate_id);
            const projectNumber = getProjectNumber(contract.project_id);
            const docsCount = contract.documents?.length || 0;
            const isDraft = contract.status === 'bozza';
            const hasSigned = !!contract.signed_pdf_url || (contract.documents || []).some((d) => d?.document_type === 'contratto_firmato');
            const canDelete = isAdmin && isDraft && !contract.project_id && !hasSigned;

            return (
              <Card
                key={contract.id}
                className={`card-abitalo cursor-pointer hover:border-[#FF7819]/50 transition-colors ${isDraft ? 'border-dashed border-gray-300 bg-gray-50' : ''}`}
                onClick={() => navigate(`/contracts/${contract.id}`)}
                data-testid={`contract-card-${contract.id}`}
              >
                <CardContent className="p-4">
                  {/* Header: cliente + indirizzo | badge + menu */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-[#3C3C3C] truncate">{contract.client_name || 'Cliente'}</h3>
                      {displayAddress && displayAddress !== 'N/D' && (
                        <p className="text-xs text-[#737373] flex items-center gap-1 mt-0.5 truncate">
                          <MapPin className="w-3 h-3 shrink-0" /> {displayAddress}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge className={`${statusConfig.color} ${isDraft ? 'opacity-80' : ''}`}>{statusConfig.label}</Badge>
                      {canDelete && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#737373]" data-testid={`contract-menu-${contract.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                                onClick={() => setDeleteTarget(contract)}
                                data-testid={`delete-draft-${contract.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Elimina bozza
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Identity */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1.5">
                    <span className="text-xs text-[#737373] font-mono">{contract.contract_number}</span>
                    {estimateNumber && <span className="text-xs text-[#9CA3AF] font-mono">· {estimateNumber}</span>}
                  </div>

                  {/* Importo */}
                  <div className="mt-2.5">
                    <p className="text-[11px] text-[#737373] uppercase tracking-wide">Importo contratto</p>
                    <p className="text-lg font-bold text-[#FF7819] leading-tight whitespace-nowrap">{formatCurrency(contract.contract_amount)}</p>
                  </div>

                  {/* Secondary info */}
                  <div className="grid grid-cols-3 gap-2 mt-2.5 pt-2.5 border-t border-gray-100 text-xs">
                    <div>
                      <p className="text-[#9CA3AF] flex items-center gap-1"><Calendar className="w-3 h-3" /> Data</p>
                      <p className="font-medium text-[#3C3C3C]">{formatDate(contract.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-[#9CA3AF] flex items-center gap-1"><FileText className="w-3 h-3" /> Doc.</p>
                      <p className="font-medium text-[#3C3C3C]">{docsCount}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#9CA3AF] flex items-center gap-1"><Briefcase className="w-3 h-3" /> Progetto</p>
                      {projectNumber ? (
                        <button
                          className="font-medium text-[#0078C8] hover:underline truncate max-w-full text-left"
                          onClick={(e) => { e.stopPropagation(); navigate(`/projects/${contract.project_id}`); }}
                          data-testid={`open-project-${contract.id}`}
                        >
                          {projectNumber}
                        </button>
                      ) : (
                        <p className="font-medium text-[#D1D5DB]">—</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Confirm delete draft modal */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-draft-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa bozza di contratto?</AlertDialogTitle>
            <AlertDialogDescription>
              Il contratto verrà rimosso dall'elenco operativo. L'operazione resterà registrata nello storico amministrativo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTarget && (
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#737373]">Cliente</span><span className="font-medium text-[#3C3C3C]">{deleteTarget.client_name}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Contratto</span><span className="font-mono text-[#3C3C3C]">{deleteTarget.contract_number}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Importo</span><span className="font-semibold text-[#3C3C3C]">{formatCurrency(deleteTarget.contract_amount)}</span></div>
              {getEstimateNumber(deleteTarget.estimate_id) && (
                <div className="flex justify-between"><span className="text-[#737373]">Preventivo</span><span className="font-mono text-[#3C3C3C]">{getEstimateNumber(deleteTarget.estimate_id)}</span></div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-draft-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteDraft(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-draft-confirm"
            >
              {deleting ? 'Eliminazione…' : 'Elimina bozza'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContractsPage;
